// ✅ src/pages/VehicleV2/AddTyreSLT.jsx
// Overflow fix: page is clipped, table scrolls inside the card.
// Browser window.confirm replaced with in-app themed ConfirmDialog.

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSave,
  FaSearch,
  FaEdit,
  FaTrash,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaExclamationTriangle,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/addTyreSLT";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const ConfirmDialog = ({
  open,
  isDark,
  title,
  message,
  confirmText,
  cancelText,
  danger,
  primaryBtnClass,
  ghostBtnClass,
  busy,
  onCancel,
  onConfirm,
}) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={busy ? undefined : onCancel}
      />

      <div
        className={`relative w-full max-w-md rounded-2xl shadow-2xl border p-6 ${
          isDark
            ? "bg-gradient-to-br from-gray-800 to-gray-900 border-white/10 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
              danger
                ? isDark
                  ? "bg-red-900/40 text-red-300"
                  : "bg-red-100 text-red-600"
                : isDark
                  ? "bg-blue-900/40 text-blue-300"
                  : "bg-blue-100 text-blue-700"
            }`}
          >
            <FaExclamationTriangle />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
              {title}
            </h3>
            <p className={`mt-2 text-sm leading-6 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {message}
            </p>
          </div>

          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={`p-2 rounded-lg ${
              isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-500"
            }`}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onCancel} disabled={busy} className={ghostBtnClass}>
            {cancelText || "Cancel"}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`${
              danger
                ? "flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white bg-red-600 hover:bg-red-700"
                : primaryBtnClass
            } ${busy ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            {confirmText || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

const AddTyreSLT = () => {
  const navigate = useNavigate();
  const query = useQuery();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const loginUserId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");
  const branchId = toInt(getLS("branchId") || getLS("branchid"), 8);

  const tyreId = toInt(query.get("tyreId") || query.get("tyreid"), 0);
  const queryType = String(query.get("type") || "").toUpperCase();
  const querySerial = String(
    query.get("serialNo") ||
      query.get("tyreSerialNo") ||
      query.get("serial") ||
      ""
  ).trim();

  const initialType = queryType === "R" ? "R" : "N";
  const openedFromListing = tyreId > 0 || !!querySerial;

  const [tyreType, setTyreType] = useState(initialType);
  const [serialNo, setSerialNo] = useState(querySerial);
  const [tyreSize, setTyreSize] = useState("");
  const [position, setPosition] = useState(openedFromListing ? "" : "Rear");

  const [serialOptions, setSerialOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);

  const [vehicleConnectInventory, setVehicleConnectInventory] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [vehicleBase, setVehicleBase] = useState("DAY");

  const basisParam = useMemo(() => {
    if (vehicleBase === "HOUR") return "Hour";
    if (vehicleBase === "KM") return "KM";
    return "Day";
  }, [vehicleBase]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [editRowId, setEditRowId] = useState(null);
  const [confirmDlg, setConfirmDlg] = useState({
    open: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    danger: true,
    action: "",
    payload: null,
  });

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const smallInputClass = `w-full min-w-[88px] px-2 py-1 border rounded-md text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const ghostBtnClass = `flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
    isDark
      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
  }`;

  const primaryBtnClass = `flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`;

  const closeConfirm = useCallback(() => {
    if (actionBusy) return;
    setConfirmDlg((prev) => ({
      ...prev,
      open: false,
      action: "",
      payload: null,
    }));
  }, [actionBusy]);

  const openConfirm = useCallback((cfg) => {
    setConfirmDlg({
      open: true,
      title: cfg.title || "Confirm",
      message: cfg.message || "Are you sure?",
      confirmText: cfg.confirmText || "Confirm",
      cancelText: cfg.cancelText || "Cancel",
      danger: cfg.danger !== false,
      action: cfg.action || "",
      payload: cfg.payload ?? null,
    });
  }, []);

  const fetchInit = async () => {
    try {
      const res = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId: toInt(loginUserId),
          branchId,
        },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load init data");
        return;
      }

      setVehicleConnectInventory(!!res.data.vehicleConnectInventory);

      if (!queryType && res.data.defaultType) {
        setTyreType(String(res.data.defaultType).toUpperCase());
      }

      if (!openedFromListing && res.data.defaultBasis) {
        const b = String(res.data.defaultBasis).toLowerCase();
        if (b.includes("hour")) setVehicleBase("HOUR");
        else if (b.includes("km")) setVehicleBase("KM");
        else setVehicleBase("DAY");
      }
    } catch (e) {
      toast.error("Server error while loading init");
    }
  };

  const fetchDropdowns = async () => {
    try {
      const res = await axios.get(`${API_BASE}/dropdowns`, {
        params: { branchId, type: tyreType },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load dropdowns");
        setSerialOptions([]);
        setSizeOptions([]);
        return;
      }

      const s = res.data.serials || [];
      const z = res.data.sizes || [];

      setSerialOptions(
        s.map((x) => ({
          value: x.tyreSerialNo ?? x.value ?? "",
          label: x.tyreSerialNo ?? x.label ?? "",
        }))
      );

      setSizeOptions(
        z.map((x) => ({
          value: x.tyreSize ?? x.value ?? "",
          label: x.tyreSize ?? x.label ?? "",
        }))
      );
    } catch (e) {
      toast.error("Server error while loading dropdowns");
      setSerialOptions([]);
      setSizeOptions([]);
    }
  };

  const fetchGrid = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/list`, {
        params: {
          branchId,
          type: tyreType,
          tyreSerialNo: serialNo,
          tyreSize,
          position,
          basis: basisParam,
          tyreId,
        },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load grid");
        setRows([]);
        return;
      }

      const resolved =
        res.data.resolvedSerialNo || res.data.list?.[0]?.tyreSerialNo || "";

      if (resolved && resolved !== serialNo) {
        setSerialNo(String(resolved));
      }

      const list = res.data.list || [];

      setRows(
        list.map((r) => {
          const local = r._local || {
            vehicleCode: r.vehicleCode ?? "",
            vehicleRegNo: r.vehicleRegNo ?? "",
            fittingDate: r.fittingDate ?? "",
            fittingHourKm: r.fittingHourKm ?? r.fittingHourKM ?? "",
            removingDate: r.removingDate ?? "",
            removingHourKm: r.removingHourKm ?? r.removingHourKM ?? "",
            runningHourKm: r.runningHourKm ?? "",
            rate: r.rate ?? "",
            tyreCostPerKm: r.tyreCostPerKm ?? "",
          };

          return {
            ...r,
            tyreSerialNoText: Array.isArray(r.tyreSerialNo)
              ? r.tyreSerialNo[0]
              : r.tyreSerialNo ?? "",
            _local: local,
          };
        })
      );
    } catch (e) {
      toast.error("Server error while loading grid");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDropdowns();
    if (!querySerial) {
      setSerialNo("");
      setTyreSize("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tyreType, branchId]);

  useEffect(() => {
    if (querySerial && !serialNo) {
      setSerialNo(querySerial);
    }
  }, [querySerial, serialNo]);

  useEffect(() => {
    fetchGrid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tyreType, serialNo, tyreSize, position, basisParam, tyreId]);

  const setRowLocal = (rowId, key, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? { ...r, _local: { ...(r._local || {}), [key]: value } }
          : r
      )
    );
  };

  const startEdit = (rowId) => setEditRowId(rowId);

  const cancelEdit = () => {
    setEditRowId(null);
    fetchGrid();
  };

  const saveRow = async (row) => {
    try {
      const L = row._local || {};
      const payload = {
        loginUserId: toInt(loginUserId),
        branchId,
        id: toInt(row.id),
        tyreId: toInt(row.tyreId || tyreId || 0),
        type: tyreType,
        vehicleBase: basisParam,
        vehicleCode: L.vehicleCode,
        vehicleRegNo: L.vehicleRegNo,
        fittingDate: L.fittingDate,
        fittingHourKm: L.fittingHourKm,
        removingDate: L.removingDate,
        removingHourKm: L.removingHourKm,
        runningHourKm: L.runningHourKm,
        rate: L.rate,
        tyreCostPerKm: L.tyreCostPerKm,
      };

      const res = await axios.post(`${API_BASE}/update-row`, payload);
      if (!res.data?.success) {
        toast.error(res.data?.message || "Update failed");
        return;
      }
      toast.success(res.data?.message || "Updated successfully");
      setEditRowId(null);
      fetchGrid();
    } catch (e) {
      toast.error("Server error while updating");
    }
  };

  const runRowAction = async (row, endpoint, failText, okText, errorText) => {
    if (!row?.id) return;

    try {
      setActionBusy(true);

      const res = await axios.post(`${API_BASE}/${endpoint}`, {
        loginUserId: toInt(loginUserId),
        branchId,
        id: toInt(row.id),
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || failText);
        return;
      }

      toast.success(res.data?.message || okText);
      setConfirmDlg((prev) => ({
        ...prev,
        open: false,
        action: "",
        payload: null,
      }));
      fetchGrid();
    } catch {
      toast.error(errorText);
    } finally {
      setActionBusy(false);
    }
  };

  const doRetread = (row) => {
    openConfirm({
      title: "Retread Tyre",
      message: "Do you really want to Retread?",
      confirmText: "Retread",
      cancelText: "Cancel",
      danger: false,
      action: "retread",
      payload: row,
    });
  };

  const doTransfer = (row) => {
    openConfirm({
      title: "Transfer Tyre",
      message: "Do you really want to Transfer?",
      confirmText: "Transfer",
      cancelText: "Cancel",
      danger: false,
      action: "transfer",
      payload: row,
    });
  };

  const doScrap = (row) => {
    openConfirm({
      title: "Scrap Tyre",
      message: "Do you really want to Scrap?",
      confirmText: "Scrap",
      cancelText: "Cancel",
      danger: true,
      action: "scrap",
      payload: row,
    });
  };

  const doDelete = (row) => {
    openConfirm({
      title: "Delete Tyre",
      message: "Do you really want to Delete?",
      confirmText: "Delete",
      cancelText: "Cancel",
      danger: true,
      action: "delete",
      payload: row,
    });
  };

  const handleConfirmOk = async () => {
    const row = confirmDlg.payload;
    if (!row) return;

    if (confirmDlg.action === "retread") {
      await runRowAction(row, "retread", "Retread failed", "Retreaded", "Server error while retread");
      return;
    }
    if (confirmDlg.action === "transfer") {
      await runRowAction(row, "transfer", "Transfer failed", "Transferred", "Server error while transfer");
      return;
    }
    if (confirmDlg.action === "scrap") {
      await runRowAction(row, "scrap", "Scrap failed", "Scrapped", "Server error while scrap");
      return;
    }
    if (confirmDlg.action === "delete") {
      await runRowAction(row, "delete-row", "Delete failed", "Deleted", "Server error while delete");
    }
  };

  const submitAll = async () => {
    try {
      setSaving(true);
      const payload = {
        branchId,
        loginUserId: toInt(loginUserId),
        type: tyreType,
        basis: basisParam,
        rows: rows.map((r) => ({
          id: toInt(r.id),
          tyreId: toInt(r.tyreId || tyreId || 0),
          useOfTyre: r.useOfTyre || "",
          vehicleCode: r._local?.vehicleCode ?? "",
          vehicleRegNo: r._local?.vehicleRegNo ?? "",
          fittingDate: r._local?.fittingDate ?? "",
          fittingHourKm: r._local?.fittingHourKm ?? "",
          removingDate: r._local?.removingDate ?? "",
          removingHourKm: r._local?.removingHourKm ?? "",
          runningHourKm: r._local?.runningHourKm ?? "",
          rate: r._local?.rate ?? "",
          tyreCostPerKm: r._local?.tyreCostPerKm ?? "",
        })),
      };

      const res = await axios.post(`${API_BASE}/submit`, payload);
      if (!res.data?.success) {
        toast.error(res.data?.message || "Submit failed");
        return;
      }
      toast.success(res.data?.message || "Submitted successfully");
      fetchGrid();
    } catch (e) {
      toast.error("Server error while submit");
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = () => fetchGrid();

  const handleViewAll = () => {
    setSerialNo("");
    setTyreSize("");
    setPosition("");
    setTimeout(() => fetchGrid(), 0);
  };

  const cellClass = `px-2 py-2 text-xs whitespace-nowrap ${
    isDark ? "text-gray-300" : "text-gray-900"
  }`;

  const actionLink = (color, disabled) =>
    `text-xs font-semibold underline transition ${
      disabled ? "opacity-40 cursor-not-allowed" : color
    }`;

  const renderInput = (row, key, extraClass = "") => (
    <td className={`px-1 py-1 ${extraClass}`}>
      <input
        disabled={editRowId !== row.id}
        value={row._local?.[key] ?? ""}
        onChange={(e) => setRowLocal(row.id, key, e.target.value)}
        className={smallInputClass}
      />
    </td>
  );

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-4 md:p-6 max-w-full overflow-x-hidden ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ADD TYRE</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-4 md:p-6 mt-6 rounded-2xl shadow-xl border max-w-full overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap min-w-0">
              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>
              <h2
                className={`text-xl md:text-2xl font-bold truncate ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                {openedFromListing ? "Edit Tyre SLT" : "Add Tyre"}
              </h2>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={submitAll}
                disabled={saving || loading}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${
                  saving || loading ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass}`}
              >
                <FaSave />
                {saving ? "Saving..." : "Submit"}
              </button>
              <button
                onClick={() => setShowFilters((prev) => !prev)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`p-4 md:p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={handleViewAll}
                    disabled={loading}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold text-white ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass}`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-6 mb-6 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={tyreType === "N"}
                    onChange={() => setTyreType("N")}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span className="text-sm font-medium">New Tyre</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={tyreType === "R"}
                    onChange={() => setTyreType("R")}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span className="text-sm font-medium">Retreaded Tyre</span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                <div className="min-w-0">
                  <label className={labelClass}>Tyre Serial No</label>
                  <select
                    value={serialNo}
                    onChange={(e) => setSerialNo(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {(serialNo || querySerial) &&
                    !serialOptions.some(
                      (o) => o.value === (serialNo || querySerial)
                    ) ? (
                      <option value={serialNo || querySerial}>
                        {serialNo || querySerial}
                      </option>
                    ) : null}
                    {serialOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Tyre Size / Ply Rating</label>
                  <select
                    value={tyreSize}
                    onChange={(e) => setTyreSize(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {sizeOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Position</label>
                  <select
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">All</option>
                    <option value="Front">Front</option>
                    <option value="Rear">Rear</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4 md:gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={vehicleBase === "HOUR"}
                    onChange={() => setVehicleBase("HOUR")}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span className="text-sm font-medium">Hour Base Vehicle</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={vehicleBase === "KM"}
                    onChange={() => setVehicleBase("KM")}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span className="text-sm font-medium">Km Base Vehicle</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={vehicleBase === "DAY"}
                    onChange={() => setVehicleBase("DAY")}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <span className="text-sm font-medium">Day Base Vehicle</span>
                </label>
              </div>
            </div>
          )}

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No Records found
                </div>
              </div>
            ) : (
              <>
                <table
                  className={`w-full min-w-[1080px] text-xs ${
                    isDark ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      {[
                        "S.No",
                        "Tyre Serial No",
                        "Tyre Size",
                        "Position",
                        "Vehicle Code",
                        "Vehicle Reg No",
                        "Fitting Date",
                        "Fit Hr/KM",
                        "Removing Date",
                        "Rem Hr/KM",
                        "Run Hr/KM",
                        "Rate",
                        "Cost/Km",
                        "Status",
                        "Actions",
                        "Branch",
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-2 py-3 text-left font-semibold whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {rows.map((r, idx) => {
                      const editing = editRowId === r.id;
                      return (
                        <tr
                          key={r.id || idx}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                          } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                        >
                          <td className={cellClass}>{r.slno ?? idx + 1}</td>
                          <td
                            className={`px-2 py-2 text-xs font-semibold whitespace-nowrap max-w-[140px] truncate ${
                              isDark ? "text-green-400" : "text-green-600"
                            }`}
                            title={r.tyreSerialNoText || ""}
                          >
                            {r.tyreSerialNoText || "-"}
                          </td>
                          <td className={cellClass}>{r.tyreSize || "-"}</td>
                          <td className={cellClass}>{r.tyrePosition || "-"}</td>
                          {renderInput(r, "vehicleCode")}
                          {renderInput(r, "vehicleRegNo")}
                          {renderInput(r, "fittingDate")}
                          {renderInput(r, "fittingHourKm")}
                          {renderInput(r, "removingDate")}
                          {renderInput(r, "removingHourKm")}
                          {renderInput(r, "runningHourKm")}
                          {renderInput(r, "rate")}
                          {renderInput(r, "tyreCostPerKm")}
                          <td className={cellClass}>{r.status || r.useOfTyre || "-"}</td>
                          <td className="px-2 py-2">
                            {editing ? (
                              <div className="flex items-center gap-2 whitespace-nowrap">
                                <button
                                  type="button"
                                  onClick={() => saveRow(r)}
                                  className="text-green-600 text-xs font-semibold underline"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  className="text-gray-500 text-xs font-semibold underline"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 flex-wrap min-w-[160px]">
                                <button
                                  type="button"
                                  onClick={() => startEdit(r.id)}
                                  className="text-yellow-500 text-base"
                                  title="Edit"
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => doRetread(r)}
                                  className={actionLink(
                                    isDark
                                      ? "text-blue-400"
                                      : "text-blue-600",
                                    false
                                  )}
                                >
                                  Retread
                                </button>
                                <button
                                  type="button"
                                  onClick={() => doTransfer(r)}
                                  className={actionLink(
                                    isDark
                                      ? "text-indigo-300"
                                      : "text-indigo-600",
                                    false
                                  )}
                                >
                                  Transfer
                                </button>
                                <button
                                  type="button"
                                  onClick={() => doScrap(r)}
                                  className={actionLink(
                                    isDark
                                      ? "text-yellow-300"
                                      : "text-yellow-700",
                                    false
                                  )}
                                >
                                  Scrap
                                </button>
                                <button
                                  type="button"
                                  onClick={() => doDelete(r)}
                                  className="text-red-500 text-base"
                                  title="Delete"
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            )}
                          </td>
                          <td className={cellClass}>{r.branchName || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="p-4 flex justify-end">
                  <button
                    onClick={submitAll}
                    disabled={saving || loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold text-white ${
                      saving || loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass}`}
                  >
                    <FaSave />
                    {saving ? "Saving..." : "Submit"}
                  </button>
                </div>
              </>
            )}
          </div>

          <ConfirmDialog
            open={confirmDlg.open}
            isDark={isDark}
            title={confirmDlg.title}
            message={confirmDlg.message}
            confirmText={confirmDlg.confirmText}
            cancelText={confirmDlg.cancelText}
            danger={confirmDlg.danger}
            primaryBtnClass={primaryBtnClass}
            ghostBtnClass={ghostBtnClass}
            busy={actionBusy}
            onCancel={closeConfirm}
            onConfirm={handleConfirmOk}
          />

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default AddTyreSLT;